import { useTranslation } from "react-i18next";
import { QUESTIONNAIRE_STATUSES, showErrorToast, showSuccessToast } from "../../../../utils";
import { Box, Button, chakra, Divider, Flex, Heading, useToast } from "@chakra-ui/react";
import { EsgLabel, FormInput } from "../../../../components/Forms";
import { NavigationTabs } from "../../../../components/NavigationTabs";
import { useForm } from "react-hook-form";
import { useCreateLocation, useUpdateLocation } from "../../../../hooks/query/supplier-questionnaire/location";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import {
  supplierQuestionnaireKeys,
  useDefaultSupplierQuestionnaireConfig,
  useFinalizeSupplierQuestionnaire,
  useGetSupplierQuestionnaireStructure,
  useUpdateFinancialYearEndMonth
} from "../../../../hooks/query";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { mapLocationsData } from "./helpers";
import { Fragment, useEffect } from "react";
import { FinancialYearEndMonthSelector } from "../FinancialYearEndMonthSelector";
import { LABEL_TYPES, OTHER_OPTION } from "../FinancialYearEndMonthSelector/FinancialYearEndMonthSelector";
import * as React from "react";
import { Answer } from "../../../SupplierPhysicalRiskQuestionnaire/components/PhysicalRiskAssessmentForm/components";

function Location({ data, returnToListRequested, declineReturnToListRequest, returnToList, isPreview }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierQuestionnaire.location"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const toast = useToast();
  const financialYearFormId = "loc-questionnaire-financial-year-form";

  const financialYearFormDefaultValues = {
    financialYearEndMonth: data?.financialYearEndMonth?.month,
    otherClarification: data?.financialYearEndMonth?.otherClarification
  };

  const handleError = (err) => {
    let errorMessage = err.message;
    if (err.response?.status === 409 && err.response?.data?.parameters?.resourceName === "SupplierQuestionnaireLocation") {
      errorMessage = t("duplicatedLocationNameMsg");
    }
    showErrorToast(toast, errorMessage);
  };

  const {
    data: structure
  } = useGetSupplierQuestionnaireStructure({
    onError: err => handleError(err)
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetCreateLocationForm
  } = useForm({});

  const {
    register: financialYearRegister,
    handleSubmit: handleFinancialYearSubmit,
    formState: financialYearFormState,
    setValue,
    watch
  } = useForm({
    defaultValues: financialYearFormDefaultValues
  });

  const financialYearEndMonthValue = watch("financialYearEndMonth");
  const otherClarificationValue = watch("otherClarification");

  const {
    mutate: finalize,
    isLoading: isLoadingOnFinalize
  } = useFinalizeSupplierQuestionnaire({
    ...useDefaultSupplierQuestionnaireConfig(),
    onSuccess: () => {
      showSuccessToast(toast, globalTranslation("supplierQuestionnaire.page.finalized"));
      returnToList();
    }
  });

  const createLocationQueryConfig = {
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      queryClient.invalidateQueries(supplierQuestionnaireKeys.supplierQuestionnaireById());
      resetCreateLocationForm();
    },
    onError: err => handleError(err),
    onSettled: () => dispatch(hideAppSpinner())
  };

  const { mutate: createLocation } = useCreateLocation(createLocationQueryConfig);
  const { mutate: updateLocation, isLoading: isLoadingOnUpdate } = useUpdateLocation();

  useEffect(() => {
    if (isLoadingOnUpdate || isLoadingOnFinalize) {
      dispatch(showAppSpinner());
    }
  }, [dispatch, isLoadingOnUpdate, isLoadingOnFinalize]);

  const onSubmitCreateLocation = (submitData) => {
    createLocation({ questionnaireId: data.id, data: { locationName: submitData.locationName } });
  };

  const isFinancialYearFormValid = () => {
    if (financialYearEndMonthValue !== null && typeof financialYearEndMonthValue !== "undefined" &&
      financialYearEndMonthValue !== "") {
      if (financialYearEndMonthValue !== OTHER_OPTION) {
        return true;
      } else {
        if (otherClarificationValue !== null && typeof otherClarificationValue !== "undefined" &&
          otherClarificationValue !== "") {
          return true;
        }
      }
    }
    return false;
  };

  const saveMethod = (locationData, isFinalize) => {
    const financialYearForm = document.getElementById(financialYearFormId);
    if (financialYearForm) {
      financialYearForm.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
    }

    if (isFinancialYearFormValid()) {
      if (locationData) {
        updateLocation({
          questionnaireId: data.id,
          id: locationData.id,
          data: locationData
        }, {
          onSuccess: () => {
            if (isFinalize) {
              finalize(data.id);
            } else {
              showSuccessToast(toast, t("locationSaved", { locationName: locationData.locationName }));
              if (returnToListRequested === true) {
                returnToList();
              }
              queryClient.invalidateQueries(supplierQuestionnaireKeys.supplierQuestionnaireById());
            }
          },
          onError: err => handleError(err),
          onSettled: () => dispatch(hideAppSpinner())
        });
      } else {
        if (isFinalize) {
          finalize(data.id);
        } else if (returnToListRequested === true) {
          returnToList();
        }
      }
    }
  };

  const { mutate: updateFinancialYearEndMonth } = useUpdateFinancialYearEndMonth({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      showSuccessToast(toast, globalTranslation("supplierQuestionnaire.page.financialYearEndSetSuccess"));
      queryClient.invalidateQueries(supplierQuestionnaireKeys.supplierQuestionnaireById());
    },
    onError: err => showErrorToast(toast, err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const onFinancialYearFormSubmit = (formData) => {
    if (formData.financialYearEndMonth !== financialYearFormDefaultValues?.financialYearEndMonth ||
      (formData.financialYearEndMonth === OTHER_OPTION &&
        formData.otherClarification !== financialYearFormDefaultValues?.otherClarification)
    ) {
      updateFinancialYearEndMonth({
        questionnaireId: data?.id,
        body: {
          month: formData.financialYearEndMonth,
          otherClarification: formData.otherClarification
        }
      });
    }
  };

  return (
    <Fragment>
      {(data.status === QUESTIONNAIRE_STATUSES.NEW || data.status === QUESTIONNAIRE_STATUSES.IN_PROGRESS) && !isPreview ?
        <Box>
          <chakra.form id="addLoction" onSubmit={handleSubmit(onSubmitCreateLocation)}>
            <Flex gap="4" marginBlockStart="5" alignItems="baseline">
              <FormInput
                placeholder={t("inputs.locationNamePlaceholder")}
                errors={errors}
                {...register("locationName", {
                  required: globalTranslation("entityRequired", { entityName: t("inputs.locationNamePlaceholder") })
                })}
              />
              <Button
                colorScheme="customScheme"
                width="32"
                type="submit"
                title={t("buttons.addLocation")}
              >
                {t("buttons.addLocation")}
              </Button>
            </Flex>
          </chakra.form>
        </Box> : null
      }
      {structure && data.locations && data.locations.length > 0 ?
        (data.status === QUESTIONNAIRE_STATUSES.NEW || data.status === QUESTIONNAIRE_STATUSES.IN_PROGRESS) && !isPreview ?
          <Flex flexFlow="column" gap={5}>
            <Divider marginBlockStart={5} />
            <chakra.form
              id={financialYearFormId}
              onSubmit={handleFinancialYearSubmit(onFinancialYearFormSubmit)}
            >
              <FinancialYearEndMonthSelector
                labelType={LABEL_TYPES.STANDARD}
                formState={financialYearFormState}
                register={financialYearRegister}
                watch={watch}
                setValue={setValue}
              />
            </chakra.form>
            <Divider marginBlockEnd={5} />
            <NavigationTabs
              elements={mapLocationsData(data.locations, data.id, structure)}
              tabTitle={null}
              subTitleAttribute={null}
              saveMethod={saveMethod}
              formSubmission={{
                allowed: true,
                requested: returnToListRequested,
                decline: declineReturnToListRequest
              }}
            />
          </Flex> :
          <Flex flexFlow="column" gap={5}>
            <Box>
              <EsgLabel text={`${globalTranslation("supplierQuestionnaire.page.financialYearEnd")} *`} />
              <Answer
                answerText={`${financialYearFormDefaultValues?.financialYearEndMonth !== OTHER_OPTION ?
                  financialYearFormDefaultValues?.financialYearEndMonth ?? "" :
                  financialYearFormDefaultValues?.otherClarification ?? ""}`} />
            </Box>
            <Divider marginBlockEnd={5} />
            <NavigationTabs
              elements={mapLocationsData(data.locations, data.id, structure)}
              tabTitle={null}
              subTitleAttribute={null}
            />
          </Flex> :
        <Heading size="md" marginBlockStart="4">
          {t("missingLocations")}
        </Heading>
      }
    </Fragment>
  );
}

export default Location;